import { Injectable } from '@angular/core'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { of } from 'rxjs'
import { catchError, map } from 'rxjs/operators'
import { environment } from 'src/environments/environment'

/** Project users — mirrors stacks-agents `src/app/api/users/`. */
@Injectable({ providedIn: 'root' })
export class UsersApiService {
  constructor(private http: HttpClient) {}

  /** Builder JWT for the stacks-agents AP */
  private agentsAuthHeaders(): HttpHeaders {
    return new HttpHeaders({ Authorization: `Bearer ${localStorage.getItem('token')}` })
  }

  getUsersCount(projectId: string): Promise<number> {
    return this.http
      .get<{ data?: number }>(`${environment.agentsUrl}v4/retrieval/users/count`, {
        headers: this.agentsAuthHeaders(),
        params: { projectId },
      })
      .pipe(
        map(body => body?.data ?? 0),
        catchError(() => of(0)),
      )
      .toPromise() as Promise<number>
  }

  getUsers(projectId: string): Promise<any[]> {
    return this.http
      .get<{ data?: any[] }>(`${environment.agentsUrl}v4/retrieval/users`, {
        headers: this.agentsAuthHeaders(),
        params: { projectId },
      })
      .pipe(
        map(body => body?.data ?? []),
        catchError(() => of([])),
      )
      .toPromise() as Promise<any[]>
  }

  getUser(projectId: string, userId: string): Promise<any | null> {
    return this.http
      .get<{ data?: any }>(`${environment.agentsUrl}v4/retrieval/users/by-id`, {
        headers: this.agentsAuthHeaders(),
        params: { projectId, userId },
      })
      .pipe(
        map(body => body?.data ?? null),
        catchError(() => of(null)),
      )
      .toPromise() as Promise<any | null>
  }

  /** Updates a project user's fields and meta. */
  async updateUser(projectId: string, userId: string, userData: any): Promise<void> {
    await this.http
      .patch(`${environment.agentsUrl}v4/update/users`,
        { projectId, userId, userData },
        { headers: this.agentsAuthHeaders() })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }

  /** Updates the caller's own last-login (and optionally projects limit) — email comes from the JWT server-side. */
  async updateUserLastLogin(projectsLimit?: number | 'unlimited'): Promise<void> {
    await this.http
      .patch(`${environment.agentsUrl}v4/update/users/last-login`,
        projectsLimit !== undefined ? { projectsLimit } : {},
        { headers: this.agentsAuthHeaders() })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }

  /** Hard-deletes a project user (cascades to meta). */
  async removeUser(projectId: string, userId: string): Promise<void> {
    await this.http
      .request('DELETE', `${environment.agentsUrl}v4/deletion/users`, {
        headers: this.agentsAuthHeaders(),
        body: { projectId, userId },
      })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }
}
